import { useState, useEffect } from 'react';
import { User, Session } from '@supabase/supabase-js';
import { supabase } from '@/integrations/supabase/client';
import { APP_URL } from '@/lib/constants';

interface Profile {
  id: string;
  user_id: string;
  display_name: string | null;
  avatar_url: string | null;
  referral_code: string | null;
  referred_by: string | null;
  bio: string | null;
  telegram_username: string | null;
  telegram_id: number | null;
  public_email: string | null;
  created_at: string;
  updated_at: string;
  is_public: boolean | null;
  is_banned: boolean | null;
  ban_until: string | null;
  ban_count: number | null;
  read_only_until: string | null;
  first_day_of_week: number | null;
  active_frame: string | null;
  active_badges: string[] | null;
  dob: string | null;
  location: string | null;
  job_title: string | null;
  status_tag: string | null;
  interests: string[] | null;
  expertise: string | null;
  can_help: string | null;
  phone: string | null;
}

export function useAuth() {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [profile, setProfile] = useState<Profile | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Set up auth state listener FIRST
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      (event, session) => {
        setSession(session);
        setUser(session?.user ?? null);
        
        // Defer profile fetch to avoid deadlock
        if (session?.user) {
          setTimeout(() => {
            fetchProfile(session.user.id);
          }, 0);
        } else {
          setProfile(null);
        }
      }
    );

    // THEN check for existing session
    supabase.auth.getSession().then(({ data: { session } }) => {
      setSession(session);
      setUser(session?.user ?? null);
      if (session?.user) {
        fetchProfile(session.user.id);
      }
      setLoading(false);
    });

    return () => subscription.unsubscribe();
  }, []);

  const fetchProfile = async (userId: string) => {
    try {
      const { data, error } = await supabase
        .from('profiles')
        .select('*')
        .eq('user_id', userId)
        .single();
      
      if (error) {
        console.error('Error fetching profile:', error);
        return;
      }
      
      if (data) {
        // Cast to Profile type ensuring all fields are included
        setProfile(data as Profile);
      }
    } catch (err) {
      console.error('Error in fetchProfile:', err);
    }
  };

  const refetchProfile = async () => {
    if (user?.id) {
      await fetchProfile(user.id);
    }
  };

  const signInWithEmail = async (email: string, password: string) => {
    const { error } = await supabase.auth.signInWithPassword({
      email,
      password,
    });
    return { error };
  };

  const signUpWithEmail = async (email: string, password: string, displayName?: string, referralCode?: string) => {
    const redirectUrl = `${APP_URL}/`;
    
    const { data, error } = await supabase.auth.signUp({
      email,
      password,
      options: {
        emailRedirectTo: redirectUrl,
        data: {
          full_name: displayName,
          referred_by: referralCode, // Pass referral code to trigger so it can process it
        },
      },
    });

    return { error };
  };

  const signInWithGoogle = async () => {
    const { error } = await supabase.auth.signInWithOAuth({
      provider: 'google',
      options: {
        redirectTo: `${APP_URL}/`,
      },
    });
    return { error };
  };

  const signInWithGitHub = async () => {
    const { error } = await supabase.auth.signInWithOAuth({
      provider: 'github',
      options: {
        redirectTo: `${APP_URL}/`,
      },
    });
    return { error };
  };

  const signOut = async () => {
    const clearAuthStorage = () => {
      if (typeof window === 'undefined') return;

      const removeAuthKeys = (store: Storage) => {
        const keys = Object.keys(store);
        keys.forEach((key) => {
          if (
            key === 'topfocus-auth-token' ||
            key.includes('auth-token') ||
            key.includes('supabase.auth.token') ||
            key.startsWith('sb-')
          ) {
            store.removeItem(key);
          }
        });
      };

      removeAuthKeys(localStorage);
      removeAuthKeys(sessionStorage);
    };

    try {
      // Try server/global sign out first
      await supabase.auth.signOut({ scope: 'global' });
    } catch (err) {
      console.warn('Global sign out failed, falling back to local sign out:', err);
    }

    try {
      // Always force local cleanup
      await supabase.auth.signOut({ scope: 'local' });
    } catch (err) {
      console.warn('Local sign out call failed, forcing storage cleanup:', err);
    }

    clearAuthStorage();
    return { error: null };
  };

  const resetPassword = async (email: string) => {
    const redirectUrl = `${APP_URL}/auth?mode=reset`;
    const { error } = await supabase.auth.resetPasswordForEmail(email, {
      redirectTo: redirectUrl,
    });
    return { error };
  };

  const updatePassword = async (newPassword: string) => {
    const { error } = await supabase.auth.updateUser({
      password: newPassword,
    });
    return { error };
  };

  return {
    user,
    session,
    profile,
    loading,
    signInWithEmail,
    signUpWithEmail,
    signInWithGoogle,
    signInWithGitHub,
    signOut,
    resetPassword,
    updatePassword,
    refetchProfile,
  };
}
